import React from 'react';
import _ from "lodash";
import styled from "styled-components";
import {
  Card,
  Row,
  Col,
  Image,
  Button,
  Form,
  FormControl,
} from "react-bootstrap";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import LoadingData from "./LoadingData";
import axios from "axios";
import Footer from "./Footer";

const StyledInput = styled(FormControl)`
  border-radius: 25px;
  border: 2px solid #e9ecef;
  padding: 12px 20px;
  font-size: 15px;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #f84f88;
    box-shadow: 0 0 0 0.2rem rgba(248, 79, 136, 0.25);
    outline: none;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const StyledCard = styled(Card)`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  border: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  height: auto;
  min-height: 90px;
  justify-content: center;
  margin-top: 12px;
  padding: 16px 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }
`;

const StyledCallBtn = styled(Button)`
  background: linear-gradient(135deg, #f84f88 0%, #e91e63 100%);
  border: none;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(248, 79, 136, 0.3);
  
  &:hover {
    transform: scale(1.1);
    background: linear-gradient(135deg, #e91e63 0%, #f84f88 100%);
    box-shadow: 0 6px 20px rgba(248, 79, 136, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const StyledAvatar = styled(Image)`
  height: 65px;
  width: 65px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #f84f88;
  box-shadow: 0 4px 12px rgba(248, 79, 136, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    border-color: #e91e63;
  }
`;

const NameText = styled.h5`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #2c3e50;
  line-height: 1.3;
`;

const TotalText = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: #f84f88;
  margin-bottom: 15px;
  text-align: center;
  padding: 12px;
  background: linear-gradient(135deg, rgba(248, 79, 136, 0.1) 0%, rgba(233, 30, 99, 0.1) 100%);
  border-radius: 12px;
  border: 1px solid rgba(248, 79, 136, 0.2);
`;

const onCall = "tel:";

const LazyImage = React.memo(({ src, alt, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div ref={imgRef} {...props}>
      {isInView && (
        <StyledAvatar
          src={src}
          alt={alt}
          onLoad={() => setIsLoaded(true)}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
      )}
    </div>
  );
});

function toSmallImage(url) {
  if (!url) return url;

  const filename = url.split('/').pop();

  // ถ้ามี newlogo_notext ให้ยกเว้น
  if (filename.includes('newlogo_notext')) {
    return url;
  }

  return url.replace(/\/([^\/]+)$/, '/small_$1');
}


const  EmployeeCard = React.memo(({ data, filterIsUpload }) => {
  return (
    <div>
      <StyledCard className="shadow-sm animate__animated animate__fadeInUp mb-3">
        <Row className="align-items-center">
          <Col xs={3} className="text-center pr-0">
            <LazyImage src={data.ImageUrl} alt={`${data.FullName} avatar`} />
          </Col>
          <Col xs={6} className="pl-2">
            <NameText>
              {data.FullName} ({data.NickName})
            </NameText>

            <p className="small mb-0" style={{ 
              color: '#6c757d', 
              fontWeight: '500',
              marginBottom: '2px'
            }}>
              {data.DeptName}
            </p>
            
            <p className="small mb-0" style={{ 
              color: '#f84f88', 
              fontWeight: '600',
              fontSize: '13px'
            }}>
              เวลาเข้า{" "}
              {data.TimeScan.replace("1970-01-01T", "").replace(
                ".000Z",
                ""
              )}{" "}
              น.
            </p>
          </Col>
          <Col xs={3} className="text-center">
            <StyledCallBtn
              href={onCall + data.MobilePhone}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="white"
                className="bi bi-telephone-fill"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"
                />
              </svg>
            </StyledCallBtn>
          </Col>
        </Row>
      </StyledCard>
    </div>
  );
});

function SignInCard() {
  const [allData, setAllData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [uploadData, setUploadData] = useState([]);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const onEnterPress = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  }, []);

  const updateInput = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // GET ATKUPLOAD DATA FROM STRAPI
  const getAtkUploadData = useCallback(() => {
    let today = new Date(Date.now());
    let thisYear = today.getFullYear();
    let thisMonth = today.getMonth() + 1;
    let thisDay = today.getDate();
    let rmupload = 0
    let rdupload = 0
    let lastDayofMonth = new Date(thisYear, thisMonth, 0).getDate();
    const currentdate = new Date();
    currentdate.setDate(currentdate.getDate());
    var oneJan = new Date(currentdate.getFullYear(),0,1);
    var numberOfDays = Math.floor((currentdate - oneJan) / (24 * 60 * 60 * 1000));
    var result = Math.ceil((numberOfDays) / 7);

    if(thisDay==lastDayofMonth)
      {
        rmupload = thisMonth + 1
        rdupload = 1
      }
    else if(thisDay < 15)
      {
        rmupload = thisMonth
        rdupload = 1
      }
    else
      {
        rmupload = thisMonth
        rdupload = 16
      }

    axios
    .get(
      'https://internal-api.fti.or.th/fti-atk-uploader-2022-s?uploadYear=' +
        thisYear +
        '&WeekNumber=' +
        result +
        "&_limit=5000"
    )
        .then((res) => {
          setUploadData(res.data);
        })
        .catch((err) => {
          console.error('Error fetching upload data:', err);
          setError('Failed to fetch upload data');
        });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get("https://line-connect.fti.or.th/getEmpSignIn");
      const sorts = response.data;
      const dataSortByTime = sorts.sort(function (a, b) {
        let c = new Date(a.TimeScan);
        let d = new Date(b.TimeScan);
        return d - c;
      });
      setIsLoading(false);
      setError(null);
      setAllData(dataSortByTime);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 3500);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData]);

  const filterIsUpload = useCallback((id) => {
    return _.some(uploadData, { EmpID: id });
  }, [uploadData]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allData;
    return allData.filter((item) => {
      return (
        (item.FullName || "").toLowerCase().includes(term) ||
        (item.NickName || "").toLowerCase().includes(term) ||
        (item.DeptName || "").toLowerCase().includes(term)
      );
    });
  }, [allData, searchTerm]);

  const memoizedData = useMemo(() => {
    return filteredData.map((item) => (
      item ? (
        <EmployeeCard
          key={item.EmployeeID}
          data={item}
          filterIsUpload={filterIsUpload}
        />
      ) : null
    ));
  }, [filteredData, filterIsUpload]);

  return (
    <>
      {error && (
        <div className="alert alert-danger m-3" style={{
          borderRadius: '12px',
          border: 'none',
          background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(220, 53, 69, 0.05) 100%)',
          color: '#721c24',
          fontWeight: '500'
        }}>
          {error}
        </div>
      )}
      
      {isLoading ? (
        <LoadingData />
      ) : (
        <TotalText> ทั้งหมด {filteredData.length} คน</TotalText>
      )}

      {memoizedData}

      <Footer>
        <Form className="d-flex px-3 pb-3">
          <StyledInput
            type="search"
            placeholder="ค้นหาชื่อ, ชื่อเล่น, หรือแผนก..."
            className="mr-2"
            aria-label="Search"
            value={searchTerm}
            onChange={updateInput}
            onKeyPress={onEnterPress}
          />
          <div className="d-flex align-items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              fill="#f84f88"
              className="bi bi-search"
              viewBox="0 0 16 16"
              style={{ cursor: 'pointer' }}
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </div>
        </Form>
      </Footer>
    </>
  );
}

export default SignInCard;
